import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import api from '@/services/api'
import { Modal } from '@/components/Modal'
import { AlertModal } from '@/components/AlertModal'
import type { AlertType } from '@/components/AlertModal'
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CreditCard,
  BookOpen,
  RefreshCw,
  ExternalLink,
} from 'lucide-react'
import { format } from 'date-fns'

// ────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────
interface Reservation {
  id: string
  booking_id: string | null
  vendor_id: number | null
  user_email: string | null
  provider: string | null
  status: string
  amount: number | null
  currency: string | null
  payment_intent_id: string | null
  expires_at: string | null
  created_via: string | null
  utm_source: string | null
  created_at: string
  updated_at: string
}

interface ReservationDetail extends Reservation {
  payment_status: string | null
  reservation_url: string | null
  bokun_booking: any | null
}

interface Pagination {
  page: number
  limit: number
  total: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

// ────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  pending: { label: 'Pending', bg: '#fef3c7', color: '#92400e' },
  confirmed: { label: 'Confirmed', bg: '#dcfce7', color: '#166534' },
  booked: { label: 'Booked', bg: '#dbeafe', color: '#1e40af' },
  expired: { label: 'Expired', bg: '#f3f4f6', color: '#374151' },
  cancelled: { label: 'Cancelled', bg: '#fee2e2', color: '#991b1b' },
  failed: { label: 'Failed', bg: '#fee2e2', color: '#991b1b' },
}

// Booking source (demand attribution) — distinct from "Provider", which is the
// supply platform (Bokun etc.). Derived from utm_source, falling back to
// created_via for non-browser channels.
const SOURCE_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  chatgpt: { label: 'ChatGPT', bg: '#dcfce7', color: '#166534' },
  claude: { label: 'Claude', bg: '#ffedd5', color: '#9a3412' },
  gemini: { label: 'Gemini', bg: '#dbeafe', color: '#1e40af' },
  perplexity: { label: 'Perplexity', bg: '#e0f2fe', color: '#0369a1' },
  copilot: { label: 'Copilot', bg: '#e0e7ff', color: '#3730a3' },
  ai_agent: { label: 'AI Agent', bg: '#dcfce7', color: '#166534' },
  google: { label: 'Google', bg: '#f3f4f6', color: '#374151' },
  bing: { label: 'Bing', bg: '#f3f4f6', color: '#374151' },
  embed: { label: 'Widget', bg: '#ede9fe', color: '#5b21b6' },
  api: { label: 'API', bg: '#f3f4f6', color: '#374151' },
  direct: { label: 'Direct', bg: '#f3f4f6', color: '#374151' },
  other: { label: 'Other', bg: '#f3f4f6', color: '#374151' },
}

/**
 * Resolve a booking's demand channel. Mirrors the API's CHANNEL_SQL and the
 * storefront's classifySource() so all three agree.
 */
function resolveSource(r: { created_via?: string | null; utm_source?: string | null }): string {
  const raw = (r.utm_source || '').toLowerCase()
  if (raw.includes('chatgpt') || raw.includes('openai')) return 'chatgpt'
  if (raw.includes('claude') || raw.includes('anthropic')) return 'claude'
  if (raw.includes('perplexity')) return 'perplexity'
  if (raw.includes('gemini') || raw.includes('bard')) return 'gemini'
  if (raw.includes('copilot')) return 'copilot'
  if (raw.includes('google')) return 'google'
  if (raw.includes('bing')) return 'bing'
  if (r.created_via === 'embed') return 'embed'
  if (r.created_via === 'agent') return 'ai_agent'
  if (r.created_via === 'api') return 'api'
  if (raw) return 'other'
  return 'direct'
}

const PAYMENT_STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  requires_payment_method: { label: 'Awaiting', bg: '#fef3c7', color: '#92400e' },
  requires_confirmation: { label: 'Pending', bg: '#fef3c7', color: '#92400e' },
  requires_action: { label: 'Action Req', bg: '#fef3c7', color: '#92400e' },
  processing: { label: 'Processing', bg: '#e0f2fe', color: '#0369a1' },
  requires_capture: { label: 'Authorised', bg: '#dbeafe', color: '#1e40af' },
  succeeded: { label: 'Captured', bg: '#dcfce7', color: '#166534' },
  canceled: { label: 'Cancelled', bg: '#fee2e2', color: '#991b1b' },
}

function Badge({ status, config }: { status: string | null; config: Record<string, { label: string; bg: string; color: string }> }) {
  if (!status) return <span style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-xs)' }}>—</span>
  const cfg = config[status] || { label: status, bg: '#f3f4f6', color: '#374151' }
  return (
    <span style={{
      padding: '0.2rem 0.6rem',
      borderRadius: '999px',
      fontSize: 'var(--text-xs)',
      fontWeight: 'var(--font-weight-medium)',
      background: cfg.bg,
      color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  )
}

function formatCurrency(amount: number | null, currency: string | null = 'USD') {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'USD' }).format(amount)
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '—'
  try { return format(new Date(dateStr), 'dd MMM yyyy') } catch { return dateStr }
}

function formatDateTime(dateStr?: string | null) {
  if (!dateStr) return '—'
  try { return format(new Date(dateStr), 'dd MMM yyyy, HH:mm') } catch { return dateStr }
}

// ────────────────────────────────────────────────
// Stat Card
// ────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: React.ElementType; color: string }) {
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.25rem' }}>
      <div style={{
        width: '44px', height: '44px', borderRadius: 'var(--radius-md)',
        background: color + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', marginBottom: '0.2rem' }}>{label}</p>
        <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-gray-900)', margin: 0 }}>{value}</p>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────
// Reservation Detail Modal
// ────────────────────────────────────────────────
function ReservationDetail({
  id,
  onClose,
  onCancel,
  onRefund,
  isCancelling,
  isRefunding,
}: {
  id: string
  onClose: () => void
  onCancel: (r: ReservationDetail) => void
  onRefund: (r: ReservationDetail) => void
  isCancelling: boolean
  isRefunding: boolean
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reservation', id],
    queryFn: () => api.reservations.get(id),
    staleTime: 60_000,
  })

  const detail: ReservationDetail | undefined = data?.data?.data

  if (isLoading) {
    return (
      <div style={{ padding: '1rem' }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ height: '36px', background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', marginBottom: '0.5rem', animation: 'pulse 1.5s ease-in-out infinite' }} />
        ))}
      </div>
    )
  }

  if (isError || !detail) {
    return <p style={{ color: 'var(--color-coral-red)', padding: '1rem' }}>Failed to load reservation details.</p>
  }

  const bokun = detail.bokun_booking
  const activity = bokun?.activityBookings?.[0]
  const contact = bokun?.passengers?.[0] ?? bokun?.mainContact

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Reservation ID', value: <span style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)' }}>{detail.id}</span> },
    { label: 'Bokun Ref', value: detail.booking_id || '—' },
    { label: 'Provider', value: detail.provider || '—' },
    {
      label: 'Booking Source',
      value: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <Badge status={resolveSource(detail)} config={SOURCE_CONFIG} />
          {detail.utm_source && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', fontFamily: 'monospace' }}>
              {detail.utm_source}
            </span>
          )}
        </span>
      ),
    },
    { label: 'Customer Email', value: detail.user_email || '—' },
    { label: 'Status', value: <Badge status={detail.status} config={STATUS_CONFIG} /> },
    { label: 'Payment', value: <Badge status={detail.payment_status} config={PAYMENT_STATUS_CONFIG} /> },
    { label: 'Amount', value: formatCurrency(detail.amount, detail.currency) },
    { label: 'Created', value: formatDateTime(detail.created_at) },
    { label: 'Expires', value: formatDateTime(detail.expires_at) },
  ]

  if (detail.reservation_url) {
    rows.push({
      label: 'Reservation URL',
      value: (
        <a href={detail.reservation_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-sky-blue)', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: 'var(--text-sm)' }}>
          Open <ExternalLink size={12} />
        </a>
      ),
    })
  }

  if (bokun) {
    rows.push(
      { label: '── Bokun Booking ──', value: '' },
      { label: 'Activity', value: activity?.title || bokun?.title || '—' },
      { label: 'Start Date', value: activity?.startDate ? formatDate(activity.startDate) : (bokun?.startDate ? formatDate(bokun.startDate) : '—') },
      { label: 'Start Time', value: activity?.startTime || bokun?.startTime || '—' },
      { label: 'Participants', value: activity?.participants != null ? String(activity.participants) : '—' },
      { label: 'Contact Name', value: contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '—' : '—' },
      { label: 'Contact Phone', value: contact?.phone || '—' },
    )
  }

  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map(({ label, value }, i) => (
            label.startsWith('──') ? (
              <tr key={i}>
                <td colSpan={2} style={{ padding: '0.75rem 0.75rem 0.25rem', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {label.replace(/──\s*/g, '')}
                </td>
              </tr>
            ) : (
              <tr key={label} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={{ padding: '0.6rem 0.75rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', width: '40%', fontWeight: 'var(--font-weight-medium)' }}>{label}</td>
                <td style={{ padding: '0.6rem 0.75rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-900)' }}>{value}</td>
              </tr>
            )
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {!['cancelled', 'failed', 'refunded', 'expired'].includes(detail.status) && (
          <button
            onClick={() => onCancel(detail)}
            disabled={isCancelling}
            style={{ background: 'transparent', color: 'var(--color-gray-600)', border: '1px solid var(--color-gray-300)' }}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel Booking'}
          </button>
        )}
        {detail.status === 'confirmed' && (
          <button
            onClick={() => onRefund(detail)}
            disabled={isRefunding}
            style={{ background: 'transparent', color: 'var(--color-coral-red)', border: '1px solid var(--color-coral-red)' }}
          >
            {isRefunding ? 'Processing…' : 'Issue Refund'}
          </button>
        )}
        <button
          onClick={onClose}
          style={{ background: 'transparent', color: 'var(--color-gray-600)', border: '1px solid var(--color-gray-300)' }}
        >
          Close
        </button>
      </div>
    </>
  )
}

// ────────────────────────────────────────────────
// Main Page
// ────────────────────────────────────────────────
export default function BookingsPage() {
  const queryClient = useQueryClient()

  // Filters
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const LIMIT = 10

  // Detail modal
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean; type: AlertType; title: string; message: string;
    onConfirm?: () => void; confirmText?: string; cancelText?: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' })
  const closeAlert = () => setAlertModal(prev => ({ ...prev, isOpen: false }))

  // ── Mutations ──────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.reservations.cancel(id),
    onSuccess: (_data, id) => {
      posthog.capture('booking_cancelled', { reservation_id: id })
      queryClient.invalidateQueries({ queryKey: ['reservations'] })
      setSelectedId(null)
      setAlertModal({ isOpen: true, type: 'success', title: 'Booking Cancelled', message: 'The booking has been cancelled successfully.' })
    },
    onError: (err: any) => {
      setAlertModal({ isOpen: true, type: 'error', title: 'Cancel Failed', message: err?.response?.data?.message || 'Failed to cancel the booking.' })
    },
  })

  const refundMutation = useMutation({
    mutationFn: (id: string) => api.reservations.refund(id, { reason: 'requested_by_customer' }),
    onSuccess: (_data, id) => {
      posthog.capture('booking_refunded', { reservation_id: id })
      queryClient.invalidateQueries({ queryKey: ['reservations'] })
      setAlertModal({ isOpen: true, type: 'success', title: 'Refund Issued', message: 'A full refund has been processed successfully.' })
    },
    onError: (err: any) => {
      setAlertModal({ isOpen: true, type: 'error', title: 'Refund Failed', message: err?.response?.data?.message || 'Failed to process the refund.' })
    },
  })

  const handleCancel = (r: { id: string; user_email: string | null; booking_id: string | null }) => {
    setAlertModal({
      isOpen: true, type: 'warning',
      title: 'Cancel Booking',
      message: `Cancel the booking for ${r.user_email || r.booking_id || r.id}? This will void the Bokun booking and release the payment hold.`,
      confirmText: 'Cancel Booking',
      cancelText: 'Keep Booking',
      onConfirm: () => { closeAlert(); cancelMutation.mutate(r.id) },
    })
  }

  const handleRefund = (r: { id: string; user_email: string | null; booking_id: string | null; amount: number | null; currency: string | null }) => {
    setAlertModal({
      isOpen: true, type: 'warning',
      title: 'Issue Refund',
      message: `Issue a full refund of ${formatCurrency(r.amount, r.currency)} to ${r.user_email || r.booking_id}?`,
      confirmText: 'Issue Refund',
      cancelText: 'Cancel',
      onConfirm: () => { closeAlert(); refundMutation.mutate(r.id) },
    })
  }

  // ── Query ──────────────────────────────────────
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reservations', page, search, status, startDate, endDate],
    queryFn: () => api.reservations.list({
      page,
      limit: LIMIT,
      search: search || undefined,
      status: status || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    }),
    staleTime: 30_000,
  })

  const reservations: Reservation[] = data?.data?.data?.items || []
  const pagination: Pagination | undefined = data?.data?.data?.pagination

  // Derived stats
  const confirmedCount = reservations.filter(r => r.status === 'confirmed' || r.status === 'booked').length
  const pendingCount = reservations.filter(r => r.status === 'pending').length
  const currency = reservations.find(r => r.currency)?.currency || 'USD'
  const totalRevenue = reservations
    .filter(r => ['confirmed', 'booked'].includes(r.status))
    .reduce((sum, r) => sum + (r.amount || 0), 0)

  // ── Filter reset ───────────────────────────────
  const resetFilters = () => {
    setSearch(''); setStatus(''); setStartDate(''); setEndDate(''); setPage(1)
  }
  const hasFilters = !!(search || status || startDate || endDate)

  // ── Loading state ──────────────────────────────
  if (isLoading) {
    return (
      <div className="container">
        <h1 style={{ marginBottom: '2rem' }}>Bookings</h1>
        {[1, 2, 3].map(i => (
          <div key={i} className="card" style={{ marginBottom: '1rem', height: '80px', background: 'var(--color-gray-100)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        ))}
      </div>
    )
  }

  // ── Error state ────────────────────────────────
  if (isError) {
    return (
      <div className="container">
        <h1 style={{ marginBottom: '2rem' }}>Bookings</h1>
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'var(--color-coral-red)', marginBottom: '1rem' }}>Failed to load bookings.</p>
          <button onClick={() => refetch()}>
            <RefreshCw size={16} style={{ marginRight: '0.5rem' }} /> Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ margin: 0 }}>Bookings</h1>
        <button
          onClick={() => { queryClient.invalidateQueries({ queryKey: ['reservations'] }) }}
          style={{ background: 'transparent', color: 'var(--color-gray-600)', border: '1px solid var(--color-gray-300)', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 'var(--text-sm)' }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Stats ── */}
      {pagination && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatCard label="Total Bookings" value={pagination.total} icon={BookOpen} color="var(--color-sky-blue)" />
          <StatCard label="Confirmed" value={confirmedCount} icon={Calendar} color="var(--color-lime-green)" />
          <StatCard label="Pending" value={pendingCount} icon={Calendar} color="var(--color-sunshine-yellow)" />
          <StatCard label="Revenue (page)" value={formatCurrency(totalRevenue, currency)} icon={CreditCard} color="var(--color-tixxly-navy)" />
        </div>
      )}

      {/* ── Filters ── */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Search */}
          <div style={{ flex: '1 1 200px', position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-gray-400)' }} />
            <input
              type="text"
              placeholder="Bokun ref or email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              style={{ width: '100%', paddingLeft: '2.25rem', paddingRight: '0.75rem', paddingTop: '0.5rem', paddingBottom: '0.5rem', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', boxSizing: 'border-box' }}
            />
          </div>

          {/* Status */}
          <div style={{ flex: '0 1 160px' }}>
            <select
              value={status}
              onChange={e => { setStatus(e.target.value); setPage(1) }}
              style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', background: 'var(--color-pure-white)', boxSizing: 'border-box' }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="booked">Booked</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Date range */}
          <div style={{ flex: '0 1 150px' }}>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setPage(1) }}
              style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ flex: '0 1 150px' }}>
            <input
              type="date"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setPage(1) }}
              style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', boxSizing: 'border-box' }}
            />
          </div>

          {/* Reset */}
          {hasFilters && (
            <button
              onClick={resetFilters}
              style={{ background: 'transparent', color: 'var(--color-gray-600)', border: '1px solid var(--color-gray-300)', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      {reservations.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <BookOpen size={40} style={{ color: 'var(--color-gray-300)', marginBottom: '1rem' }} />
          <p style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-base)', marginBottom: '0.5rem' }}>
            {hasFilters ? 'No bookings match your filters.' : 'No bookings yet.'}
          </p>
          {hasFilters && (
            <button onClick={resetFilters} style={{ background: 'transparent', color: 'var(--color-sky-blue)', border: '1px solid var(--color-sky-blue)', marginTop: '0.5rem' }}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr style={{ background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                  {['Bokun Ref', 'Customer', 'Provider', 'Booking Source', 'Amount', 'Status', 'Created'].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reservations.map((r, idx) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    style={{ borderBottom: idx < reservations.length - 1 ? '1px solid var(--color-gray-100)' : 'none', transition: 'background 0.15s', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-gray-50)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '0.875rem 1rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-tixxly-navy)', whiteSpace: 'nowrap' }}>
                      {r.booking_id || <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                      {r.user_email || <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', textTransform: 'capitalize' }}>
                      {r.provider || '—'}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      <Badge status={resolveSource(r)} config={SOURCE_CONFIG} />
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-900)', whiteSpace: 'nowrap' }}>
                      {formatCurrency(r.amount, r.currency)}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      <Badge status={r.status} config={STATUS_CONFIG} />
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', whiteSpace: 'nowrap' }}>
                      {formatDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {pagination && pagination.total_pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 1rem', borderTop: '1px solid var(--color-gray-100)', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', margin: 0 }}>
                Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, pagination.total)} of {pagination.total} bookings
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={!pagination.has_prev}
                  style={{ padding: '0.375rem 0.625rem', background: 'transparent', color: 'var(--color-gray-700)', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', cursor: pagination.has_prev ? 'pointer' : 'not-allowed', opacity: pagination.has_prev ? 1 : 0.4 }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                  Page {page} of {pagination.total_pages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination.has_next}
                  style={{ padding: '0.375rem 0.625rem', background: 'transparent', color: 'var(--color-gray-700)', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', cursor: pagination.has_next ? 'pointer' : 'not-allowed', opacity: pagination.has_next ? 1 : 0.4 }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Detail Modal ── */}
      <Modal
        isOpen={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={selectedId ? 'Booking Details' : ''}
        maxWidth="600px"
      >
        {selectedId && (
          <ReservationDetail
            id={selectedId}
            onClose={() => setSelectedId(null)}
            onCancel={handleCancel}
            onRefund={handleRefund}
            isCancelling={cancelMutation.isPending}
            isRefunding={refundMutation.isPending}
          />
        )}
      </Modal>

      {/* ── Alert Modal ── */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
    </div>
  )
}
